import { COPILOT } from '@cast/constants';
import {
  Icons,
  LabelChip,
  Switch,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { OpsPilotEnabledNotice } from './OpsPilotEnabledNotice';
import { useOpsPilotEnablement } from './useOpsPilotEnablement';

import { FailedToLoad } from '@cast/shared-messages-ui';
import { useIsOpsPilotFeatureFlagEnabled } from '../components/ai-assistant/useIsOpsPilotFeatureFlagEnabled';

export const FeatureControlSection = () => {
  const isOpsPilotFeatureFlagEnabled = useIsOpsPilotFeatureFlagEnabled();
  const {
    isAckUxEnabled,
    isEnabled,
    acknowledgedAt,
    canUpdateSettings,
    error,
    refetch,
    handleSwitchToggle,
  } = useOpsPilotEnablement();

  const showContactAdminNote = !canUpdateSettings && !isEnabled;

  return (
    <Stack gap={24} data-testid="feature-control-section">
      <Typography variant="P14R" color={tokens.theme.fg.structural.secondary}>
        Manage feature availability and access for all users in your
        organization.
      </Typography>
      {isOpsPilotFeatureFlagEnabled ? (
        <Box
          sx={{
            borderRadius: '4px',
            padding: 24,
            backgroundColor: tokens.theme.bg.structural.surface,
            boxShadow: (theme) => theme.shadows[5],
          }}
        >
          {error ? (
            <Box data-testid="error-state">
              <FailedToLoad
                title="Failed to load feature settings"
                body="Something went wrong. Please try again later."
                refresh={refetch}
                compact
              />
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="38px 1fr"
              gridTemplateAreas={`
              "switch title"
              "spacer description"
            `}
              columnGap={16}
            >
              <Stack gridArea="switch" justifyContent="flex-start" pt={2}>
                <Switch
                  size="md"
                  checked={isEnabled}
                  onChange={handleSwitchToggle}
                  disabled={!canUpdateSettings}
                  testId="ask-cast-ai-switch"
                />
              </Stack>

              <Box gridArea="title">
                <Stack direction="row" alignItems="center" gap={8} mb={8}>
                  <Typography
                    variant="h5"
                    color={colorOrToken(
                      'grey.900',
                      tokens.theme.fg.structural.primary
                    )}
                  >
                    {COPILOT}
                  </Typography>
                  <LabelChip
                    size="sm"
                    sx={{
                      backgroundColor: colorOrToken(
                        'blue.100',
                        tokens.theme.bg.categorical.blueSubtle
                      ),
                      color: colorOrToken(
                        'blue.500',
                        tokens.theme.fg.categorical.onBlueSubtle
                      ),
                    }}
                  >
                    New
                  </LabelChip>
                </Stack>
              </Box>

              <Box gridArea="description">
                <Stack gap={8}>
                  <Typography
                    variant="P14R"
                    color={colorOrToken(
                      'grey.600',
                      tokens.theme.fg.structural.secondary
                    )}
                    maxWidth="85%"
                  >
                    Give your organization access to {COPILOT}, an AI assistant
                    that answers questions about your clusters, costs, and
                    savings in natural language. All queries use only your
                    organization&apos;s data, which remains private.
                  </Typography>

                  {isAckUxEnabled && isEnabled && (
                    <OpsPilotEnabledNotice acknowledgedAt={acknowledgedAt} />
                  )}

                  {showContactAdminNote && (
                    <Stack
                      direction="row"
                      gap={6}
                      alignItems="center"
                      data-testid="opspilot-permission-note"
                      sx={{
                        color: colorOrToken(
                          'grey.500',
                          tokens.theme.fg.structural.secondary
                        ),
                      }}
                    >
                      <Icons.Info size={16} />
                      <Typography
                        variant="P12R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        To enable {COPILOT}, contact your organization
                        administrator.
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}
    </Stack>
  );
};
